import React, { useMemo, useState } from 'react';
import { kindIsPack } from '../../lib/phaseKinds';
import { publicPath } from '../../lib/publicLinks';
import { useToast } from '../../hooks/useToast';
import { hasPending } from '../../lib/portalQueue';
import { Button, FormField, Combobox, MobileScreen, MobileHeader, MobileHeaderButton, MobileActionBar, MobileCard, mobileInputCls } from '../ui';
import { IconPhone } from '../icons';
import {
    formatTime12, jobStatusPill, routeStopsFor, mapsUrlFor, combinedLegLabel, primaryRoom,
    withinRecapEditWindow, getPortalAuthHeaders, usePortalQueue, OfflineBanner,
} from './portalShared';

// ---------------------------------------------------------------------------
// Job screen — back arrow, client name + status pill, "time · type · crew
// · truck", Call. Route card + Navigate, notes cards (same tints as
// before), Crew · truck card, client photos, waivers. Pinned: Start recap
// (or "✓ Recap submitted" + Edit recap inside the 90-min window).
// ---------------------------------------------------------------------------

const routeLabelCls = 'text-[10px] font-bold tracking-wider uppercase';

// Waivers — lists the client's waivers (signed / pending, "Open →" for
// unsigned) and lets the lead add one. add_waiver stays a LIVE online
// flow (it opens the signing tab on success), so it isn't queued.
// Types that already have an unsigned waiver on the latest estimate are
// disabled; the server de-dupes too (reused: true).
function WaiversCard({ job, waivers, waiverTypes, token, onChanged, canAdd }) {
    const { show } = useToast();
    const [selectedType, setSelectedType] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const types = waiverTypes || [];

    const pendingTypeKeys = useMemo(() => {
        const set = new Set();
        for (const w of (waivers || [])) if (!w.signed_at && w.waiver_type) set.add(w.waiver_type);
        return set;
    }, [waivers]);

    const typeLabel = (key) => {
        const t = types.find(t => t.key === key);
        return t ? (t.shortLabel || t.title || key) : key;
    };

    const submit = async () => {
        if (!selectedType) { setError('Required'); return; }
        setSubmitting(true);
        setError(null);
        try {
            const authHeaders = await getPortalAuthHeaders();
            const res = await fetch('/api/crew-portal-data', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify({
                    action: 'add_waiver',
                    token: token || undefined,
                    clientId: job.client_id,
                    waiverType: selectedType,
                    notes: notes || null,
                }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `Server returned ${res.status}`);
            // Open the public waiver page in a new tab so the customer can
            // sign on the same device.
            if (body.waiver?.token) {
                const url = publicPath('waiver', body.waiver.token, 'src=crew_portal');
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            show(body.reused ? 'Waiver already pending — opened existing link' : 'Waiver created — hand the phone to the customer', 'success');
            setSelectedType('');
            setNotes('');
            if (onChanged) onChanged();
        } catch (err) {
            show(err.message || 'Add failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileCard title={`Waivers (${(waivers || []).length})`}>
            {(waivers || []).length > 0 ? (
                <ul className="space-y-1.5 mb-3">
                    {waivers.map(w => (
                        <li key={w.id} className="flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-slate-800 truncate">{typeLabel(w.waiver_type)}</div>
                                {w.signed_at
                                    ? <div className="text-emerald-700 text-[11px]">Signed{w.signed_name ? ` by ${w.signed_name}` : ''}</div>
                                    : <div className="text-amber-700 text-[11px]">Pending signature</div>}
                            </div>
                            {!w.signed_at && (
                                <a href={publicPath('waiver', w.token, 'src=crew_portal')} target="_blank" rel="noopener noreferrer"
                                    className="text-xs font-semibold text-red-600 flex-shrink-0">Open →</a>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-xs text-slate-400 mb-3">No waivers on file for this client.</p>
            )}
            {canAdd && (
                <>
                    <FormField label="Waiver type" className="mb-3" error={error}>
                        <Combobox
                            items={types.map(t => ({
                                value: t.key,
                                label: `${t.title || t.shortLabel || t.key}${pendingTypeKeys.has(t.key) ? ' (pending)' : ''}`,
                                disabled: pendingTypeKeys.has(t.key),
                            }))}
                            value={selectedType}
                            onChange={v => { setSelectedType(v); setError(null); }}
                            placeholder="— Select waiver —"
                            ariaLabel="Waiver type"
                        />
                    </FormField>
                    <FormField label="Notes (optional)" className="mb-3">
                        <input type="text" className={mobileInputCls} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why is this needed?" />
                    </FormField>
                    <Button variant="secondary" full loading={submitting} disabled={!selectedType} onClick={submit}>
                        Add &amp; open signing link
                    </Button>
                </>
            )}
        </MobileCard>
    );
}

function Note({ tone, label, children }) {
    const tones = {
        amber: 'bg-amber-50 border-amber-200 text-amber-900',
        slate: 'bg-slate-50 border-slate-200 text-slate-800',
        red: 'bg-red-50 border-red-200 text-red-900',
        indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    };
    return (
        <div className={`rounded-xl border p-3 text-[13px] leading-snug ${tones[tone] || tones.slate}`}>
            <span className="font-bold">{label}: </span>
            <span className="whitespace-pre-wrap">{children}</span>
        </div>
    );
}

export default function JobScreen({ job, clients, allCrew, allTrucks, allPhotos, allWaivers, allEstimates, waiverTypes, validDate, crewId, token, onChanged, onBack, onStartRecap, justQueued = false, tabs, strip }) {
    const queue = usePortalQueue();
    const client = clients.find(c => c.id === job.client_id);
    const clientName = client?.name || 'Client';
    const isLead = job.leader_id === crewId;
    const isCompletedJob = job.status === 'completed';
    const editWindow = withinRecapEditWindow(job);
    const canRecap = isLead && (!isCompletedJob || editWindow);
    const submittedAt = job.recap_submitted_at;
    const recapQueued = justQueued || hasPending(`recap:${job.id}`, queue);

    const jobType = job.job_label || (job.type || '').replace(/_/g, ' ');
    const jobCrew = (job.crew_ids || []).map(id => allCrew.find(c => c.id === id)).filter(Boolean);
    const jobTrucks = (job.truck_ids || []).map(id => allTrucks.find(t => t.id === id)).filter(Boolean);
    const clientWaivers = (allWaivers || []).filter(w => w.client_id === job.client_id);
    const latestEstimate = (allEstimates || [])
        .filter(e => e.client_id === job.client_id)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    const clientPhotos = (allPhotos || []).filter(p => p.client_id === job.client_id);
    const photosByRoom = useMemo(() => {
        const grouped = {};
        clientPhotos.forEach(p => {
            const room = primaryRoom(p);
            if (!grouped[room]) grouped[room] = [];
            grouped[room].push(p);
        });
        return grouped;
    }, [clientPhotos]);

    const { middleStops, afterStops } = routeStopsFor(job, clients, allEstimates);
    const mapsUrl = mapsUrlFor(job, middleStops, afterStops);
    const pill = jobStatusPill(job, validDate);
    const tripTag = job.trip_tags && job.trip_tags[validDate];
    const tripLabel = typeof tripTag === 'string' ? tripTag : (tripTag?.tag || null);
    const tripDayIndex = Number(job._trip_day_index) || 0;
    const tripDayTotal = Number(job._trip_day_total) || 0;
    const dayKinds = Array.isArray(job._trip_day_kinds) && job._trip_day_kinds.length > 0
        ? job._trip_day_kinds
        : (job.phase_kind ? [job.phase_kind] : []);
    const hasTripBanner = tripDayIndex > 0 && (tripDayTotal > 1 || dayKinds.length > 1);
    const isPackDay = job.type === 'PACKING' || kindIsPack(job.phase_kind);

    const sub = [formatTime12(job.time) || 'Time TBD', jobType, `${jobCrew.length} crew`, jobTrucks.length ? jobTrucks.map(t => t.name).join(', ') : null]
        .filter(Boolean).join(' · ');
    const phone = (client?.phone || '').replace(/[^\d+]/g, '');

    let action = null;
    if (isLead && submittedAt) {
        action = (
            <MobileActionBar className="flex-col items-stretch gap-1.5">
                <div className="text-center text-[13px] font-bold text-emerald-600">✓ Recap submitted</div>
                {canRecap && <Button variant="secondary" size="lg" full onClick={onStartRecap}>Edit recap</Button>}
            </MobileActionBar>
        );
    } else if (canRecap) {
        action = (
            <MobileActionBar>
                <Button size="lg" full onClick={onStartRecap}>Start recap</Button>
            </MobileActionBar>
        );
    }

    return (
        <MobileScreen
            header={(
                <MobileHeader
                    title={clientName}
                    status={pill}
                    sub={sub}
                    onBack={onBack}
                    right={phone ? <MobileHeaderButton href={`tel:${phone}`} label="Call"><IconPhone size={18} /></MobileHeaderButton> : null}
                />
            )}
            strip={strip}
            action={action}
            tabs={tabs}
        >
            {recapQueued && <OfflineBanner what="recap" />}

            {hasTripBanner && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                    <div className="text-[10px] font-bold tracking-wider text-amber-700 uppercase">Day {tripDayIndex} of {tripDayTotal}</div>
                    <div className="text-sm font-bold text-amber-900">{combinedLegLabel(dayKinds, job._trip_destination_address)}</div>
                </div>
            )}
            {tripLabel && (
                <div className="text-[12px] font-semibold text-amber-800"><span className="inline-block px-1.5 rounded-md bg-amber-100">{tripLabel}</span></div>
            )}
            {/* Pack-day materials nudge (Jaylen 2026-08-07) — materials ARE
                the work on a pack day. */}
            {isPackDay && !submittedAt && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-800">
                    📦 Pack day — count every box, roll of paper, and tape as you go. Log it in the recap's Materials step.
                </div>
            )}

            <MobileCard title="Route">
                <div className="text-[13px] text-slate-700 space-y-0.5">
                    <div className={`${routeLabelCls} text-slate-400`}>From</div>
                    <div>{job.location_name && <strong className="text-slate-900">{job.location_name} — </strong>}{job.from_address || 'TBD'}</div>
                    {middleStops.map((addr, i) => (
                        <React.Fragment key={i}>
                            <div className={`${routeLabelCls} text-amber-500 pt-1.5`}>Stop {i + 1}</div>
                            <div>{addr}</div>
                        </React.Fragment>
                    ))}
                    <div className={`${routeLabelCls} text-slate-400 pt-1.5`}>To</div>
                    <div>{job.to_address || 'TBD'}</div>
                    {afterStops.map((addr, i) => (
                        <React.Fragment key={'after-' + i}>
                            <div className={`${routeLabelCls} text-purple-500 pt-1.5`}>Then · after unload</div>
                            <div>{addr}</div>
                        </React.Fragment>
                    ))}
                </div>
                {mapsUrl && (
                    <Button variant="secondary" size="lg" full className="mt-3" onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}>
                        🧭 Navigate
                    </Button>
                )}
            </MobileCard>

            {(!!job.sqft || job.stairs || job.elevator || job.duration) && (
                <MobileCard title="Details">
                    <div className="flex flex-wrap gap-1.5">
                        {job.duration && <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700">Est. {job.duration}h</span>}
                        {!!job.sqft && <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700">{job.sqft} sq ft</span>}
                        {job.stairs && <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-amber-100 text-amber-800">Stairs — {job.levels || '?'} levels</span>}
                        {job.elevator && <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-blue-100 text-blue-800">Elevator</span>}
                    </div>
                </MobileCard>
            )}

            {job.heavy_items && <Note tone="amber" label="Heavy items">{job.heavy_items}</Note>}
            {job.special_notes && <Note tone="slate" label="Notes">{job.special_notes}</Note>}
            {job.dispatcher_notes && <Note tone="red" label="Dispatch">{job.dispatcher_notes}</Note>}
            {job.crew_notes && <Note tone="indigo" label="📋 Crew notes">{job.crew_notes}</Note>}

            <MobileCard title="Crew · truck">
                <div className="flex flex-wrap gap-1.5">
                    {jobCrew.map(c => (
                        <span key={c.id} className="text-xs font-semibold px-2.5 py-1 rounded-md text-white" style={{ background: c.display_color || '#6b7280' }}>
                            {c.name}{c.id === job.leader_id ? ' (L)' : ''}
                        </span>
                    ))}
                    {jobTrucks.map(t => (
                        <span key={t.id} className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200">🚚 {t.name}</span>
                    ))}
                    {jobCrew.length === 0 && jobTrucks.length === 0 && <span className="text-xs text-slate-400">Not assigned yet.</span>}
                </div>
            </MobileCard>

            {Object.keys(photosByRoom).length > 0 && (
                <MobileCard title={`Photos (${clientPhotos.length})`}>
                    {Object.entries(photosByRoom).map(([room, photos]) => (
                        <div key={room} className="mb-3 last:mb-0">
                            <div className="text-xs font-semibold text-slate-600 mb-1">{room}</div>
                            <div className="grid grid-cols-3 gap-1.5">
                                {photos.map(p => (
                                    <a key={p.id} href={p.url || p.public_url || '#'} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden bg-slate-100 block">
                                        <img src={p.thumbnail_url || p.url || p.public_url} alt={p.caption || ''} loading="lazy" className="w-full h-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                </MobileCard>
            )}

            {/* Lead-only: waivers (view + add). The server still validates
                auth; gating here keeps the UI uncluttered for the rest of
                the crew. */}
            {isLead && latestEstimate && (
                <WaiversCard job={job} waivers={clientWaivers} waiverTypes={waiverTypes} token={token} onChanged={onChanged} canAdd />
            )}
        </MobileScreen>
    );
}
